import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';
import ImageSlant from '../../common/image-slant';
import festive from '../../../assets/christmas25/festive.webp';
import tea from '../../../assets/christmas25/tea2.webp';
import grotto from '../../../assets/christmas25/grotto.webp';
import search from '../../../assets/christmas25/search.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const Hero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.25), rgba(0,0,0,0.65)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const HeroInner = styled.div`
  max-width: 1400px;
  width: 100%;
  padding: 3rem 2rem;
  color: white;
`;

const HeroTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
`;

const HeroSub = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
`;

const HeroBadge = styled.div`
  display: inline-block;
  margin-top: 0.75rem;
  background: rgba(255,255,255,0.9);
  color: ${props => props.theme.colors.accent};
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 1px;
  border-radius: 6px;
  padding: 0.35rem 0.6rem;
  border: 1px solid rgba(255,255,255,0.7);
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 0 2rem 3rem;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 2.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    margin-top: 1rem;
  }
`;

const FullWidthRow = styled.div`
  grid-column: 1 / -1;
`;

const Card = styled.section.attrs({ className: 'bf-surface' })`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: '';
    position: absolute;
    left: 0; bottom: 0;
    width: 80px; height: 3px;
    background: ${props => props.theme.colors.accent};
  }
`;

const Body = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
  p { margin: 0 0 1rem; }
`;

const Steps = styled.ol`
  counter-reset: step;
  list-style: none;
  padding: 0; margin: 1.25rem 0 0;
`;

const StepItem = styled.li.attrs({ className: 'bf-surface' })`
  position: relative;
  padding: 1rem 1rem 1rem 3.5rem;
  background: ${props => props.theme.colors.offWhite};
  border-radius: 10px;
  margin-bottom: 0.75rem;

  &::before {
    counter-increment: step;
    content: counter(step);
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    width: 28px; height: 28px;
    border-radius: 50%;
    background: ${props => props.theme.colors.accent};
    color: white;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700;
  }
`;

const ExtrasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const Extra = styled.a.attrs({ className: 'bf-surface' })`
  display: block;
  text-decoration: none;
  color: inherit;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
  background: white;
  transition: transform 0.2s ease;

  &:hover { transform: translateY(-3px); }
`;

const ExtraImage = styled.div`
  height: 180px;
  background-image: ${props => `url(${props.src})`};
  background-size: cover;
  background-position: center;
`;

const ExtraBody = styled.div`
  padding: 1rem 1.25rem;
`;

const ExtraTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.25rem 0;
`;

const ExtraText = styled.p`
  margin: 0; color: #555; font-size: 0.95rem;
`;

const InfoStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const InfoBox = styled.div.attrs({ className: 'bf-surface' })`
  background: ${props => props.theme.colors.offWhite};
  padding: 1rem 1.25rem;
  border-radius: 10px;
`;

const CTAButton = styled.a.attrs({ className: 'cta-button' })`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.9rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 700;
  margin-top: 1.25rem;
  transition: all 0.2s ease;
  &:hover { opacity: 0.95; transform: translateY(-2px); }
`;

const FamilySaverNotice = styled.section`
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 0 2rem;
`;

const SaverCard = styled.div.attrs({ className: 'bf-surface' })`
  background: linear-gradient(180deg, #ffffff 0%, #fffdf8 100%);
  border-radius: 12px;
  padding: 2rem 2.25rem;
  box-shadow: 0 12px 28px rgba(0,0,0,0.08);
  border: 1px solid #f0e8da;
  outline: 2px solid rgba(201, 152, 94, 0.08);
  outline-offset: -1px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 1.25rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SaverHeading = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  line-height: 1.3;
`;

const SaverText = styled.p`
  margin: 0;
  color: #333;
  font-size: 1rem;
  line-height: 1.6;
`;

const SaverActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

// Refinements to card presentation
const SaverHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.25rem;
`;

const SaverBadge = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 1px;
  color: ${props => props.theme.colors.accent};
  background: #fff7ec;
  border: 1px solid #f3dfc5;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
`;

// Family Saver tiles
const SaverGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 1.25rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const SaverTile = styled.div.attrs({ className: 'bf-surface' })`
  background: ${props => props.theme.colors.offWhite};
  border: 1px solid #eee6d9;
  border-radius: 10px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 8px 18px rgba(0,0,0,0.05);
  min-height: 120px;
`;

const SaverTileTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.05rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.6rem 0;
`;

const SaverTileText = styled.p`
  margin: 0;
  color: #444;
  font-size: 0.98rem;
  line-height: 1.7;
`;
// Ticket types at a glance
const TypesSection = styled.section`
  max-width: 1400px;
  margin: 0 auto 2rem;
  padding: 2rem 2rem 0;
`;

const TypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  
  @media (min-width: 1600px) { 
    grid-template-columns: repeat(5, 1fr); 
  }
  @media (max-width: 1200px) { 
    grid-template-columns: repeat(2, 1fr); 
  }
  @media (max-width: 768px) { 
    grid-template-columns: 1fr; 
  }
`;

const TypeCard = styled.a.attrs({ className: 'bf-surface' })`
  display: block;
  text-decoration: none;
  color: inherit;
  background: ${({ darkMode }) => darkMode ? 'rgba(5,5,8,0.95)' : 'white'};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: ${({ darkMode }) => darkMode ? '0 25px 80px rgba(0,0,0,0.45)' : '0 8px 20px rgba(0,0,0,0.08)'};
  border: ${({ darkMode }) => darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)'};
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.theme.colors.accent};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ darkMode }) => darkMode ? '0 30px 90px rgba(0,0,0,0.55)' : '0 12px 30px rgba(0,0,0,0.12)'};
    border-color: ${({ darkMode, theme }) => darkMode ? 'rgba(255,255,255,0.15)' : theme.colors.accent};
    
    &::before {
      transform: scaleX(1);
    }
  }

  &:active {
    transform: translateY(-2px);
  }
`;

const TypeTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  color: ${({ darkMode, theme }) => darkMode ? '#fff3d6' : theme.colors.primary};
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TypeDescription = styled.p`
  margin: 0;
  color: ${({ darkMode }) => darkMode ? 'rgba(255,255,255,0.8)' : '#555'};
  font-size: 0.95rem;
  line-height: 1.6;
`;

const Badge = styled.span.attrs(({ variant }) => ({
  className: variant === 'separate' ? 'bf-chip bf-chip--alert' : 'bf-chip'
}))`
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  background: ${props => props.variant === 'separate' ? '#FFE9E9' : '#F2EEE9'};
  border: 1px solid ${props => props.variant === 'separate' ? '#FFB3B3' : '#D9C3A6'};
  border-radius: 999px;
  padding: 0.2rem 0.6rem;
  margin-left: 0.5rem;
`;

// Early Bird prices block
const PricesIntro = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0 0 1rem;
  flex-wrap: wrap;
`;

const EarlyBirdTag = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: #fff;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.9rem;
`;

const PriceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin: 0.5rem 0 1.25rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const PriceCard = styled.div.attrs({ className: 'bf-surface' })`
  background: ${props => props.theme.colors.offWhite};
  border-radius: 10px;
  padding: 1rem 1.25rem;
`;

const PriceType = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0;
  border-top: 1px solid rgba(0,0,0,0.06);

  &:first-of-type { border-top: none; }

  span:first-child {
    color: #555;
  }
  span:last-child {
    font-weight: 700;
    color: ${props => props.theme.colors.accent};
  }
`;

const YourFestiveVisit = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const meta = {
    title: 'Your Christmas Visit | Christmas 2025 at Belvoir Castle',
    description: 'Build your perfect Christmas package: book your entry ticket first, then add Santa experiences, Festive Afternoon Tea and more for a Magical Night Before Christmas.',
    canonical: 'https://www.belvoircastle.com/christmas/your-festive-visit'
  };

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={festive} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Your Christmas Visit at Belvoir Castle" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={festive} />
        <meta name="twitter:site" content="@belvoircastle" />
        <meta name="twitter:url" content={meta.canonical} />
        <meta name="twitter:image:alt" content="Your Christmas Visit at Belvoir Castle" />
      </Helmet>

      <Hero image={festive}>
        <HeroInner>
          <HeroTitle>Your Christmas Visit</HeroTitle>
          <HeroSub>Build the perfect Christmas package for a Magical Night Before Christmas</HeroSub>
          <HeroBadge>Family Saver available</HeroBadge>
        </HeroInner>
      </Hero>

      <FamilySaverNotice>
        <SaverCard>
          <div>
            <SaverHeader>
              <SaverHeading>Family Saver</SaverHeading>
            </SaverHeader>
            <SaverText>
              {isBlackFriday ? (
                <>
                  Black Friday unlocks an additional 20% off Entry and Santa Tickets when you book during Black Friday. Keep adding names to watch the savings stack automatically with additional Family Saver savings.
                </>
              ) : (
                <>
                  The more people you add, the more you save — discover savings as you build your group. Add your whole family to unlock our most welcoming prices this Christmas.
                </>
              )}
            </SaverText>
          </div>
          <SaverActions>
            <CTAButton href="/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14">Start Family Saver Booking</CTAButton>
          </SaverActions>
        </SaverCard>
      </FamilySaverNotice>

      <TypesSection id="mice-trail">
        <PriceGrid>
          <PriceCard>
            <PriceType>Adult</PriceType>
            <PriceRow>
              <span>Online Price</span>
              <span>£30.00</span>
            </PriceRow>
            <PriceRow>
              <span>Ticket Office</span>
              <span>£33.00</span>
            </PriceRow>
          </PriceCard>
          <PriceCard>
            <PriceType>Child</PriceType>
            <PriceRow>
              <span>Online Price</span>
              <span>£15.00</span>
            </PriceRow>
            <PriceRow>
              <span>Ticket Office</span>
              <span>£16.50</span>
            </PriceRow>
          </PriceCard>
          <PriceCard>
            <PriceType>Concession</PriceType>
            <PriceRow>
              <span>Online Price</span>
              <span>£27.00</span>
            </PriceRow>
            <PriceRow>
              <span>Ticket Office</span>
              <span>£29.70</span>
            </PriceRow>
          </PriceCard>
        </PriceGrid>
        <SectionTitle>Tickets at a Glance</SectionTitle>
        <TypesGrid>
          <TypeCard 
            href="/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14"
            darkMode={isBlackFriday}
          >
            <TypeTitle darkMode={isBlackFriday}>Entry Ticket</TypeTitle>
            <TypeDescription darkMode={isBlackFriday}>
              Day access to the Castle and grounds. Includes the Mice Trail.
            </TypeDescription>
          </TypeCard>
          <TypeCard 
            href="/christmas/mice-trail"
            darkMode={isBlackFriday}
          >
            <TypeTitle darkMode={isBlackFriday}>
              Mice Trail <Badge>Included with Entry</Badge>
            </TypeTitle>
            <TypeDescription darkMode={isBlackFriday}>
              A charming family trail throughout the Castle. Included with Entry and Search for Santa.
            </TypeDescription>
          </TypeCard>
          <TypeCard 
            href="/christmas/search-for-santa"
            darkMode={isBlackFriday}
          >
            <TypeTitle darkMode={isBlackFriday}>
              Search for Santa <Badge variant="separate">Separate Ticket</Badge>
            </TypeTitle>
            <TypeDescription darkMode={isBlackFriday}>
              Magical family trail through the Castle with snacks, drinks and a present for the child. Weekends only, starts at 8:30am before the Castle opens, includes full day access and the Mice Trail.
            </TypeDescription>
          </TypeCard>
          <TypeCard 
            href="https://www.belvoircastle.com/christmas/santas-grotto"
            darkMode={isBlackFriday}
          >
            <TypeTitle darkMode={isBlackFriday}>
              Santa's Grotto <Badge>Add-on</Badge>
            </TypeTitle>
            <TypeDescription darkMode={isBlackFriday}>
              Meet Santa in his grotto at Belvoir Retail Village. Add after booking entry tickets.
            </TypeDescription>
          </TypeCard>
          <TypeCard 
            href="/christmas/festive-afternoon-tea"
            darkMode={isBlackFriday}
          >
            <TypeTitle darkMode={isBlackFriday}>
              Festive Afternoon Tea <Badge>Add-on</Badge>
            </TypeTitle>
            <TypeDescription darkMode={isBlackFriday}>
              Seasonal sweets and savouries in our elegant tearooms. Add after entry.
            </TypeDescription>
          </TypeCard>
        </TypesGrid>
      </TypesSection>

      <Content>
        <FullWidthRow>
          <Card>
            <SectionTitle>How Family Saver Works</SectionTitle>
            <Body>
              <p>The Family Saver is designed to welcome more of your loved ones this Christmas. Build your group and your combined price updates automatically — the more people you add, the more you save. Explore different group sizes to discover your best price during booking.</p>
            </Body>
            <SaverGrid>
              <SaverTile>
                <SaverTileTitle>Bring Your Group</SaverTileTitle>
                <SaverTileText>Add your partner, children, grandparents and friends — Family Saver works across your whole group.</SaverTileText>
              </SaverTile>
              <SaverTile>
                <SaverTileTitle>Automatic Savings</SaverTileTitle>
                <SaverTileText>As you add people, your combined price updates instantly to reflect available savings.</SaverTileText>
              </SaverTile>
              <SaverTile>
                <SaverTileTitle>Try Different Sizes</SaverTileTitle>
                <SaverTileText>Experiment with your group before checkout to find the right mix for your visit.</SaverTileText>
              </SaverTile>
            </SaverGrid>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
              <CTAButton href="/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14">Start Family Saver Booking</CTAButton>
            </div>
          </Card>
        </FullWidthRow>

        <Card>
          <SectionTitle>Build Your Perfect Christmas Package</SectionTitle>
          <Body>
            <p>Create your ideal day at Belvoir Castle this Christmas. Choose your path: book your <strong>Entry Ticket</strong> for a full Castle day, or book a <strong>Search for Santa Entry Ticket</strong> (separate ticket). Then add the extras that make your visit magical.</p>
            <Steps>
              <StepItem>Book your <strong>Entry Ticket</strong> to the Castle <em>or</em> book a <strong>Search for Santa Entry Ticket</strong> (separate ticket)</StepItem>
              <StepItem>If you chose Entry Ticket, add festive extras: <strong>Santa’s Grotto</strong> and <strong>Festive Afternoon Tea</strong></StepItem>
              <StepItem>Confirm your timeslots and <strong>build your package</strong></StepItem>
              <StepItem>Enjoy a <strong>Magical Night Before Christmas</strong> at the Castle</StepItem>
            </Steps>
            <CTAButton href="/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14">Start Your Booking</CTAButton>
          </Body>
        </Card>

        <Card>
          <SectionTitle>Popular Add-ons</SectionTitle>
          <ExtrasGrid>
            <Extra href="https://www.belvoircastle.com/christmas/santas-grotto">
              <ExtraImage src={grotto} />
              <ExtraBody>
                <ExtraTitle>Santa's Grotto</ExtraTitle>
                <ExtraText>Meet Santa in his grotto at Belvoir Retail Village. Add after entry.</ExtraText>
              </ExtraBody>
            </Extra>
            <Extra href="/christmas/festive-afternoon-tea">
              <ExtraImage src={tea} />
              <ExtraBody>
                <ExtraTitle>Festive Afternoon Tea</ExtraTitle>
                <ExtraText>Seasonal sweet and savoury treats in elegant tearooms.</ExtraText>
              </ExtraBody>
            </Extra>
            <Extra href="/christmas/search-for-santa">
              <ExtraImage src={search} />
              <ExtraBody>
                <ExtraTitle>Search for Santa <Badge variant="separate">Separate Ticket</Badge></ExtraTitle>
                <ExtraText>Separate ticket. Early morning family trail through the Castle.</ExtraText>
              </ExtraBody>
            </Extra>
          </ExtrasGrid>
        </Card>
      </Content>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem 2rem' }}>
        <Card>
          <SectionTitle>Plan Your Day</SectionTitle>
          <InfoStrip>
            <InfoBox><strong>Opening Times</strong><br/>29 Nov – 31 Dec, 10am – 5pm</InfoBox>
            <InfoBox><strong>Getting Here</strong><br/>Belvoir Castle, Grantham, NG32 1PA</InfoBox>
            <InfoBox><strong>Good to Know</strong><br/>Mice Trail included with entry; pre-booking recommended</InfoBox>
          </InfoStrip>
        </Card>
      </div>

      <ImageSlant 
        image={festive}
        title="Make it Magical"
        description="Add Santa experiences, Festive Afternoon Tea, and more to create a day you'll never forget."
        buttonText="Start Your Booking"
        buttonAction={() => window.location.assign('/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14')}
        imagePosition="left"
        darkMode={isBlackFriday}
      />

      <div style={{ margin: '3rem 0' }}>
        <NewsletterSignUp tag="Christmas 2025" />
      </div>
    </Page>
  );
};

export default YourFestiveVisit;

